import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Gift, KeyRound, Search } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { inputCls } from "@/components/Form";
import { GrantDialog, CreditsDialog, ResetPwDialog } from "@/components/admin/ClientDialogs";
import { fmtDoc, fmtPhoneBr } from "@/components/CompleteClientInfo";

const DIALOGS = { grant: GrantDialog, credits: CreditsDialog, reset: ResetPwDialog };
const ACTIONS = [["grant", CalendarPlus, "Licença"], ["credits", Gift, "Grátis"], ["reset", KeyRound, "Senha"]];

const validity = (c) => {
  if (c.role === "admin") return "Admin";
  if (c.subscription_status === "expired") return "Vencida";
  if (c.plan_id === "pro") return c.plan_expires_at ? `Até ${fmtDate(c.plan_expires_at)}` : "Sem vencimento";
  return "Teste grátis";
};

function ClientRow({ c, onAction }) {
  return (
    <tr className="border-t border-slate-100" data-testid={`client-row-${c.user_id}`}>
      <td className="px-4 py-3"><p className="font-semibold text-slate-900">{c.name}</p><p className="text-xs text-slate-500">{c.email}</p></td>
      <td className="px-4 py-3 text-sm" data-testid={`client-document-${c.user_id}`}>{c.document ? fmtDoc(c.document) : "—"}</td>
      <td className="px-4 py-3 text-sm" data-testid={`client-phone-${c.user_id}`}>{c.phone ? fmtPhoneBr(c.phone) : "—"}</td>
      <td className="px-4 py-3 text-sm">{c.plan_id === "pro" ? "Profissional" : "Teste"}</td>
      <td className="px-4 py-3 text-sm" data-testid={`client-validity-${c.user_id}`}>{validity(c)}</td>
      <td className="px-4 py-3 text-sm">{c.usage ?? 0}</td>
      <td className="px-4 py-3 text-sm font-semibold" data-testid={`client-bonus-${c.user_id}`}>{c.bonus_credits || 0}</td>
      <td className="px-4 py-3">
        {c.role !== "admin" && (
          <div className="flex flex-wrap gap-1.5">
            {ACTIONS.map(([k, Icon, label]) => (
              <button key={k} data-testid={`client-${k}-btn-${c.user_id}`} onClick={() => onAction(k, c)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 px-3 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100">
                <Icon className="h-3.5 w-3.5" />{label}
              </button>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

export default function ClientsPanel() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setClients(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => {
    const t = q.toLowerCase(), d = q.replace(/\D/g, "");
    return clients.filter((c) => `${c.name} ${c.email} ${c.restaurant_name || ""}`.toLowerCase().includes(t) || (d.length >= 3 && `${c.document || ""} ${(c.phone || "").replace(/\D/g, "")}`.includes(d)));
  }, [clients, q]);
  const Dlg = dlg && DIALOGS[dlg.kind];
  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input data-testid="clients-search-input" placeholder="Buscar por nome, e-mail, CPF/CNPJ ou telefone" className={`${inputCls} pl-10`} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="clients-table">
        <table className="w-full min-w-[980px] text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Cliente", "CPF/CNPJ", "Telefone", "Plano", "Validade", "Usadas", "Bônus", "Ações"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{shown.map((c) => <ClientRow key={c.user_id} c={c} onAction={(kind, client) => setDlg({ kind, client })} />)}</tbody>
        </table>
        {!shown.length && <p className="p-6 text-center text-sm text-slate-500" data-testid="clients-empty">Nenhum cliente encontrado.</p>}
      </div>
      {Dlg && <Dlg client={dlg.client} onClose={() => setDlg(null)} onDone={load} />}
    </div>
  );
}
